import { Facebook, Meta } from '../features/creative/MetaAdPreview'
import facebookAdGlobe from '../assets/facebookAdGlobe.png'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import React, { ReactNode, useState } from 'react'
import { Button, TextField, Typography } from '@mui/material'
import styled from '@emotion/styled'
import { e0Grey } from '../colors'
import { theme } from '../mui'
import { fetchGraphQL } from '../relay'
import { toast } from 'react-toastify'

const PreviewWrapper = styled.div`
  position: relative;
  width: 20rem;
  height: 34rem;
  padding: 1rem;
  background-color: ${e0Grey};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0.375em;
  font-size: 0.875rem;
  color: ${theme.palette.text.disabled};
  cursor: pointer;
  transition: 0.5s;
  &:hover {
    box-shadow:
      rgb(0 0 0 / 20%) 0px 3px 3px -2px,
      rgb(0 0 0 / 14%) 0px 3px 4px 0px,
      rgb(0 0 0 / 12%) 0px 1px 8px 0px;
  }
`

const Ad = ({
  text,
  image,
  onClick,
}: {
  text: String
  image: string
  onClick: any
}) => {
  return (
    <PreviewWrapper onClick={onClick}>
      <Facebook.Wrapper>
        <Facebook.Header>
          <Facebook.Image src="https://purposely-assets-app.s3.us-west-2.amazonaws.com/demo/299151594_542847277636658_166254585268086790_n.jpg" />
          <Facebook.HeaderText>
            Fountainwood at Lake Houston
            <Facebook.HeaderTextSubheader>
              Sponsored ·&nbsp;
              <Facebook.HeaderGlobe src={facebookAdGlobe} />
            </Facebook.HeaderTextSubheader>
          </Facebook.HeaderText>
          <MoreHorizIcon />
        </Facebook.Header>
        <Facebook.PrimaryText>{text}</Facebook.PrimaryText>
        <div>
          <Meta.AdImage src={image} />
        </div>
        <Facebook.Footer>
          <Facebook.FooterText>
            <Facebook.Headline>Assisted Living</Facebook.Headline>
            Schedule a tour
          </Facebook.FooterText>
          <Facebook.CallToAction variant="outlined">
            Contact Us
          </Facebook.CallToAction>
        </Facebook.Footer>
      </Facebook.Wrapper>
    </PreviewWrapper>
  )
}

const AdWithPerformance = ({
  text,
  image,
  rate,
  onClick,
}: {
  text: String
  image: string
  rate: number
  onClick: any
}) => {
  return (
    <div style={{ marginRight: '1rem' }}>
      {rate}% Result Rate
      <Ad text={text} image={image} onClick={onClick} />
    </div>
  )
}

const AdWithoutPerformance = ({
  text,
  image,
  onClick,
}: {
  text: String
  image: string
  onClick: any
}) => {
  return (
    <div style={{ marginRight: '1rem' }}>
      <Ad text={text} image={image} onClick={onClick} />
    </div>
  )
}

export const Demo = () => {
  const [selected, setSelected] = useState(null as ReactNode | null)
  const [imagePrompt, setImagePrompt] = useState('')
  const [textPrompt, setTextPrompt] = useState('')
  const [textFieldContents, setTextFieldContents] = useState('')
  const [loading, setLoading] = useState(false)
  const [header, setHeader] = useState('Select Inspiration')
  const [canLaunch, setCanLaunch] = useState(false)

  const ad1StartingPrompt = 'senior citizen happy in a room no faces'
  const ad2StartingPrompt =
    'dinner plate with steak shallow depth of field photorealistic'
  const ad3StartingPrompt = 'senior citizen dining no faces'
  const ad1Text =
    "At Fountainwood, we're committed to offering top-tier memory care services for your loved one every single day."
  const ad2Text =
    'Experience luxury independent living, premium amenities and curated spaces at Fountainwood at Lake Houston today!'
  const ad3Text =
    "At Fountainwood, we're dedicated to making Mom's assisted living experience the best possible. View our services."

  const ad1 = (
    <AdWithPerformance
      text={ad1Text}
      image="https://purposely-assets-app.s3.us-west-2.amazonaws.com/META/0b319d23-6d7a-4fce-bd39-5ec3d0262ec7/41acaf7b-683c-4e4e-b749-ed8745db7158.jpg"
      rate={0.49}
      onClick={() => {
        setSelected(ad1)
        setImagePrompt(ad1StartingPrompt)
        setTextPrompt(`Write a sentence like "${ad1Text}`)
        setHeader('Hone Ad')
      }}
      key="1"
    />
  )
  const ad2 = (
    <AdWithPerformance
      text={ad2Text}
      image="https://purposely-assets-app.s3.us-west-2.amazonaws.com/demo/307053616_23851704593520674_7678427744203849861_n.jpg"
      rate={1.03}
      onClick={() => {
        setSelected(ad2)
        setImagePrompt(ad2StartingPrompt)
        setTextPrompt(`Write a sentence like "${ad2Text}`)
        setHeader('Hone Ad')
      }}
      key="2"
    />
  )
  const ad3 = (
    <AdWithPerformance
      text={ad3Text}
      image="https://purposely-assets-app.s3.us-west-2.amazonaws.com/META/4c7857f7-3503-4298-bfd5-7737a60e59db/fc242c91-6b03-4100-a46c-a3bb6901e2ad.jpg"
      rate={0.55}
      onClick={() => {
        setSelected(ad3)
        setImagePrompt(ad3StartingPrompt)
        setTextPrompt(`Write a sentence like "${ad3Text}`)
        setHeader('Hone Ad')
      }}
      key="3"
    />
  )

  const [ads, setAds] = useState([ad2, ad3, ad1])

  let body
  if (selected == null) {
    body = ads
  } else {
    body = (
      <>
        {selected}
        <div style={{ marginTop: '1rem' }}>
          <TextField
            onChange={(event) => setTextFieldContents(event.target.value)}
          />
          <Button
            style={{ marginLeft: '1rem' }}
            disabled={loading}
            onClick={() => {
              setImagePrompt(`${imagePrompt} ${textFieldContents}`)
              setLoading(true)
              fetchGraphQL(
                `
              query DemoQuery($input: GenerateAdsRequest!) {
                generateAds(input: $input) {
                  image
                  text
                }
              }
            `,
                {
                  input: {
                    imagePrompt: imagePrompt,
                    textPrompt: textPrompt,
                  },
                }
              )
                .then((response) => {
                  setLoading(false)
                  setHeader('Select Best Ad')
                  setAds(
                    response.data.generateAds.map(
                      (generatedContent: { image: string; text: string }) => {
                        const ad = (
                          <AdWithoutPerformance
                            text={generatedContent.text}
                            image={generatedContent.image}
                            onClick={() => {
                              setSelected(ad)
                              setHeader('Hone Ad')
                              setCanLaunch(true)
                            }}
                            key={generatedContent.text}
                          />
                        )
                        return ad
                      }
                    )
                  )
                  setSelected(null)
                })
                .catch(() => setLoading(false))
            }}
          >
            Hone Ad
          </Button>
        </div>
        <Button
          onClick={() => toast.success('Successfully Launched ad in Meta')}
          style={{ height: '2rem', margin: '1rem' }}
          disabled={!canLaunch}
        >
          Launch Ad
        </Button>
      </>
    )
  }
  return (
    <>
      <Typography sx={{ margin: '1rem 0' }} variant="h4">
        {header}
      </Typography>
      <div style={{ display: 'flex' }}>{body}</div>
    </>
  )
}
